import type { ItemKind, ItemSummary, Stage } from '@mystiq/contracts';

const KIND_LABEL: Record<ItemKind, string> = { note: 'Note', file: 'File', extracted_text: 'Text', transcript: 'Transcript', site: 'Site' };

export function kindLabel(item: Pick<ItemSummary, 'kind' | 'mimeType'> & { capture?: ItemSummary['capture'] }): string {
  if (item.capture === 'voice_note') return 'Voice note';
  if (item.capture === 'meeting') return 'Meeting';
  if (item.kind === 'file' && item.mimeType) {
    if (item.mimeType.startsWith('text/') || item.mimeType === 'application/json') return 'Text file';
    if (item.mimeType === 'application/pdf') return 'PDF';
    if (item.mimeType.startsWith('image/')) return 'Image';
    if (item.mimeType.startsWith('audio/')) return 'Audio';
    if (item.mimeType.startsWith('video/')) return 'Video';
  }
  return KIND_LABEL[item.kind];
}

const pad = (value: number) => String(value).padStart(2, '0');

/** A position in a recording as players show it: 4:05, or 1:02:07 past an hour (counted down to the second). */
export function clockTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours ? `${hours}:${pad(minutes)}:${pad(total % 60)}` : `${minutes}:${pad(total % 60)}`;
}

/** A length, to the nearest second. */
export const formatDuration = (ms: number) => clockTime(Math.round(ms / 1000) * 1000);

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function timeAgo(iso: string, now = Date.now()): string {
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** How long until a future time, e.g. "in 6 d"; "now" once it has passed. */
export function timeUntil(iso: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  if (seconds <= 0) return 'now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `in ${Math.max(1, minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} h`;
  return `in ${Math.round(hours / 24)} d`;
}

const STAGE_NAME: Record<Stage['kind'], string> = {
  extract_text: 'Text extraction',
  transcribe: 'Transcription',
  enrich_metadata: 'AI details',
  transform: 'AI writing',
  generate_site: 'Site',
  purge: 'Permanent deletion',
  file_item: 'Filing',
};

export function stageName(stage: Pick<Stage, 'kind'>): string {
  return STAGE_NAME[stage.kind];
}

/** Plain words for a stage's state (state words are bold ink in the UI; no color coding). */
export function stageState(stage: Pick<Stage, 'status' | 'errorCode'>): string {
  switch (stage.status) {
    case 'queued':
      return stage.errorCode ? 'Retrying' : 'Waiting';
    case 'running':
      return 'Working';
    case 'succeeded':
      return 'Done';
    case 'partial':
      return 'Done for part of the content';
    case 'unsupported':
      return 'Not available';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Stopped';
  }
}

/** A short processing summary for a row's meta line, or null when nothing needs saying. */
export function processingSummary(item: ItemSummary & { stages?: Stage[] }): string | null {
  if (item.storageState && item.storageState !== 'available') {
    return item.storageState === 'failed' ? 'Upload failed' : 'Uploading';
  }
  return null;
}

/** Renders a search snippet's **highlights** as segments. */
export function snippetParts(snippet: string): Array<{ text: string; hit: boolean }> {
  return snippet.split('**').map((text, index) => ({ text, hit: index % 2 === 1 }));
}
